/**
 * What this file does:
 * It's a helper "component" to render AI sections based on the page mode
 * i.e live view mode or live edit mode
 */
import React from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { useEditorToolbar } from '@atlaskit/editor-common/toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { SeparatorPosition } from '@atlaskit/editor-toolbar';
import { ToolbarSection } from '@atlaskit/editor-toolbar';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type { AIPlugin } from '../../../aiPluginType';

const usePluginState = conditionalHooksFactory(
	() => expValEquals('platform_editor_toolbar_aifc_patch_3', 'isEnabled', true),
	(api?: ExtractInjectionAPI<AIPlugin> | undefined) => {
		const { editorViewMode } = useEditorToolbar();
		return {
			editorViewMode,
		};
	},
	(api?: ExtractInjectionAPI<AIPlugin> | undefined) => {
		const { mode } = useSharedPluginStateWithSelector(api, ['editorViewMode'], (states) => {
			return {
				mode: states.editorViewModeState?.mode,
			};
		});

		return {
			editorViewMode: mode,
		};
	},
);

export const AISection = ({
	api,
	children,
	hasSeparator,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	children: React.ReactNode;
	hasSeparator?: SeparatorPosition;
}): React.JSX.Element | null => {
	const { editorViewMode } = usePluginState(api);
	const isEdit = editorViewMode === 'edit' || editorViewMode === undefined;

	if (!isEdit) {
		return null;
	}

	return <ToolbarSection hasSeparator={hasSeparator}>{children}</ToolbarSection>;
};

export const AIViewSection = ({
	api,
	children,
	hasSeparator,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	children: React.ReactNode;
	hasSeparator?: SeparatorPosition;
}): React.JSX.Element | null => {
	const { editorViewMode } = usePluginState(api);
	const isView = editorViewMode === 'view';

	if (!isView) {
		return null;
	}

	return <ToolbarSection hasSeparator={hasSeparator}>{children}</ToolbarSection>;
};
